import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, BatteryMedium, ShieldCheck, CheckCircle2, Apple, ArrowRight } from "lucide-react";
import { fetchUsatoVetrina, euro, mediaUrl } from "@/lib/store";

const Badge = ({ children, cls }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}>{children}</span>
);

export const UsedShowcase = () => {
  const [items, setItems] = useState([]);
  const scroller = useRef(null);

  useEffect(() => { fetchUsatoVetrina().then(setItems).catch(() => setItems([])); }, []);
  if (!items.length) return null;

  const scroll = (dir) => scroller.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  return (
    <section data-testid="used-showcase" className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:py-16">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">Vetrina Usato Garantito iPhix</h2>
          <p className="mt-1 text-sm font-medium text-neutral-500">100% originali Apple · Testati e garantiti 12 mesi da iPhix</p>
        </div>
        <div className="hidden gap-2 sm:flex">
          <button data-testid="used-showcase-prev" onClick={() => scroll(-1)} className="rounded-full border border-neutral-200 p-2 text-neutral-600 hover:border-neutral-900 hover:text-neutral-900"><ChevronLeft className="h-5 w-5" /></button>
          <button data-testid="used-showcase-next" onClick={() => scroll(1)} className="rounded-full border border-neutral-200 p-2 text-neutral-600 hover:border-neutral-900 hover:text-neutral-900"><ChevronRight className="h-5 w-5" /></button>
        </div>
      </div>

      <div ref={scroller} className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="used-showcase-track">
        {items.map((d) => (
          <Link key={d.id} to={`/usato/${d.slug || d.id}`} data-testid={`used-showcase-card-${d.id}`}
            className="group relative w-[78%] shrink-0 snap-start overflow-hidden rounded-3xl border border-neutral-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-neutral-900 hover:shadow-2xl sm:w-[300px]">
            <div className="relative aspect-[4/5] overflow-hidden bg-neutral-100">
              {d.image ? <img src={mediaUrl(d.image)} alt={`${d.marca} ${d.modello}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                : <div className="flex h-full w-full items-center justify-center text-neutral-300">Foto</div>}
              {d.promo_attiva && <span className="absolute left-3 top-3 rounded-full bg-rose-600 px-2.5 py-1 text-xs font-bold text-white shadow">PROMO</span>}
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-neutral-900">{d.marca} {d.modello}</h3>
              <p className="text-sm text-neutral-500">{[d.memoria, d.colore].filter(Boolean).join(" · ")}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {d.is_apple && <Badge cls="bg-neutral-900 text-white"><Apple className="h-2.5 w-2.5" /> 100% Originale Apple</Badge>}
                <Badge cls="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-2.5 w-2.5" /> Testato</Badge>
                <Badge cls="bg-blue-100 text-blue-700"><ShieldCheck className="h-2.5 w-2.5" /> {d.warranty_months || 12} mesi</Badge>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                {d.batteria_pct != null && <span className="inline-flex items-center gap-1"><BatteryMedium className="h-3.5 w-3.5" /> {d.batteria_pct}%</span>}
                {d.grado && <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-bold text-neutral-700">Grado {d.grado}</span>}
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                {d.promo_attiva ? (<>
                  <span className="text-xl font-bold text-rose-600">{euro(d.promo_price_cents)}</span>
                  <span className="text-sm text-neutral-400 line-through">{euro(d.prezzo_vendita_cents)}</span>
                </>) : <span className="text-xl font-bold text-neutral-900">{euro(d.prezzo_vendita_cents)}</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-2 text-center">
        <Link data-testid="used-showcase-all" to="/usato" className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-6 py-3 text-sm font-semibold text-white transition-all hover:scale-[1.02] hover:bg-black">
          Vedi tutto l'Usato <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
};
